import { FormEvent, useEffect, useState } from 'react';
import { BadgePercent, RotateCcw } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';

type LoyaltyData = {
  plans: Array<{ id: string; name: string; active: boolean }>;
  memberships: Array<{ id: string; patientId: string; planId: string; status: string }>;
  wallets: Array<{ id: string; patientId: string; pointsBalance: number }>;
  transactions: Array<{ id: string; patientId: string; points: number; reason: string }>;
};

export default function LoyaltyPage() {
  const { fetchJson } = useApiClient();
  const [data, setData] = useState<LoyaltyData | null>(null);
  const [planName, setPlanName] = useState('');
  const [error, setError] = useState('');

  async function load() {
    try {
      setError('');
      setData(await fetchJson<LoyaltyData>('/loyalty'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load loyalty data.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function createPlan(event: FormEvent) {
    event.preventDefault();
    if (!planName.trim()) return;
    await fetchJson('/loyalty/plans', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: planName, benefits: {} }),
    });
    setPlanName('');
    await load();
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Memberships and loyalty</p>
          <h1 className="text-2xl font-semibold text-slate-900">Optional rewards foundation</h1>
        </div>
        <button type="button" onClick={() => void load()} className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-2 text-sm font-medium"><RotateCcw size={16} /> Refresh</button>
      </div>
      {error ? <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</div> : null}
      <form onSubmit={createPlan} className="flex gap-2 rounded-lg border border-slate-200 bg-white p-4">
        <input value={planName} onChange={(event) => setPlanName(event.target.value)} placeholder="Membership plan name" className="flex-1 rounded-md border border-slate-300 px-3 py-2 text-sm" />
        <button className="rounded-md bg-slate-900 px-4 py-2 text-sm font-semibold text-white">Add plan</button>
      </form>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Membership plans" rows={(data?.plans || []).map((row) => [row.name, row.active ? 'Active' : 'Inactive'])} />
        <Panel title="Patient memberships" rows={(data?.memberships || []).map((row) => [row.patientId, `${row.planId} · ${row.status}`])} />
        <Panel title="Loyalty wallets" rows={(data?.wallets || []).map((row) => [row.patientId, `${row.pointsBalance} points`])} />
        <Panel title="Loyalty transactions" rows={(data?.transactions || []).map((row) => [row.patientId, `${row.points} · ${row.reason}`])} />
      </div>
    </div>
  );
}

function Panel({ title, rows }: { title: string; rows: Array<[string, string]> }) {
  return <div className="rounded-lg border border-slate-200 bg-white p-5"><div className="flex items-center gap-2 text-sm font-semibold text-slate-900"><BadgePercent size={16} /> {title}</div><div className="mt-3 divide-y divide-slate-100">{rows.length ? rows.map(([label, value], index) => <div key={`${label}-${index}`} className="flex items-center justify-between py-2 text-sm"><span className="text-slate-700">{label}</span><span className="text-slate-500">{value}</span></div>) : <div className="py-3 text-sm text-slate-500">No rows yet.</div>}</div></div>;
}
